import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { hooks } from '@lifesize/clients.sdk';
import { ReactComponent as BackIcon } from '@lifesize/ux-assets/vectors/svgIcons/chevron-back.svg';
import { makeStyles, Theme } from '@material-ui/core';
import { closeInCallModal } from 'actions/InCallModal';
import VbbSettings from 'components/VbbSettings/VbbSettings';
import AvSettings from 'components/AvSettings/AvSettings';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { isCallDisconnected } from 'utils/call-utils';
import colors from 'styles/variables/baseColors.module.scss'; // bridge from scss variables to js

interface Props {
  className?: string;
  isStartCallFlow?: boolean;
  onToggleAvSettings?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const AvSettingsModal = (props: Props) => {
  const classes = useStyles({});
  const avSettingsModalRef = useRef<any>(null);
  const dispatch = useDispatch();
  const [showVbbSettings, setShowVbbSettings] = useState(false);
  const activeCall = hooks.useCallState();
  const isInCall = !isCallDisconnected(activeCall);
  const extension = activeCall.dialString;
  useOnClickOutside(avSettingsModalRef, () => dispatch(closeInCallModal()));

  const onToggleVbbSettings = () => {
    setShowVbbSettings(!showVbbSettings);
  };

  const containerClasses = clsx([classes.container, isInCall ? classes.inCall : '', props.className]);

  if (showVbbSettings) {
    return (
      <div className={containerClasses}>
        <VbbSettings isStartCallFlow={props.isStartCallFlow} onToggleVbbSettings={onToggleVbbSettings} />
      </div>
    );
  }

  return (
    <div className={containerClasses} ref={avSettingsModalRef}>
      {!!props.onToggleAvSettings && (
        <section className={classes.header}>
          <a onClick={props.onToggleAvSettings} className={classes.backBtn}>
            <BackIcon />
            <label>
              <FormattedMessage id="components.vbbSettings.btnBack" defaultMessage="Back" />
            </label>
          </a>
        </section>
      )}
      {isInCall && (
        <header>
          <FormattedMessage
            id="components.avSettingsModal.changeSettings"
            defaultMessage="Change camera and audio sources"
          />
        </header>
      )}
      <AvSettings extension={extension} isInCall={isInCall} onToggleVbbSettings={onToggleVbbSettings} />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'center',
    color: colors.abbey,
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    maxWidth: 700, // for consistency with children
    width: '100%',

    '& > header': {
      color: colors.thunder,
      fontWeight: theme.typography.fontWeightLight,
      fontSize: '32px',
      margin: `0 0 ${theme.spacing(3)}px`
    }
  },
  header: {
    display: 'flex',
    flex: '0 0 auto',
    marginBottom: theme.spacing(3),
    width: '100%'
  },
  backBtn: {
    color: colors.chambray,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 12,

    '& svg': {
      height: 16,
      marginRight: 8,
      width: 'auto'
    },
    '& label': {
      cursor: 'inherit',
      lineHeight: 1,
      marginTop: '0.25em'
    }
  },
  inCall: {
    height: 'auto',
    marginTop: 0,
    minHeight: 484,
    padding: theme.spacing(4),
    width: 796 // includes left/right padding so width = 700
  },
  closeButton: {
    width: 40,
    height: 40,
    backgroundColor: 'white',
    position: 'absolute',
    right: 8,
    top: -42,
    animationName: 'fade-in-later',
    animationDuration: '0.4s',
    animationTimingFunction: 'ease-in',
    backgroundRepeat: 'no-repeat',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    outline: 'none',
    overflow: 'hidden',
    padding: 4,
    zIndex: 100
  }
}));

export default AvSettingsModal;
